import {
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  type MetaFunction,
  redirect,
} from "react-router";
import {
  Form,
  useLoaderData,
  useActionData,
  useNavigation,
  Link,
} from "react-router";

export const meta: MetaFunction = () => [{ title: "MediaScribe - Sign In" }];
import {
  getSession,
  commitSession,
  SESSION_MAX_AGE_DEFAULT,
  SESSION_MAX_AGE_REMEMBER,
} from "~/session.server";
import {
  authenticateUser,
  getMsSession,
  addAccountToSession,
  checkLoginRateLimit,
  recordLoginAttempt,
  clearFailedLoginAttempts,
} from "~/utils/ms-auth.server";
import {
  verifyTurnstileWithCache,
  getTurnstileSiteKey,
  getClientIP,
  isTurnstileChallengeRequired,
} from "~/utils/turnstile.server";
import { audit } from "~/utils/logger.server";
import { TurnstileWidget } from "~/components/auth/TurnstileWidget";
import { Button } from "~/components/button/Button";
import { Alert } from "~/components/alert/Alert";
import { FormInput } from "~/components/form-input";

interface LoaderData {
  turnstileSiteKey: string | undefined;
  turnstileRequired: boolean;
  message?: string;
  isAddAccountMode: boolean;
  existingAccountEmail?: string;
  returnTo?: string;
}

export const loader = async ({
  request,
}: LoaderFunctionArgs): Promise<LoaderData | Response> => {
  const url = new URL(request.url);
  const mode = url.searchParams.get("mode");
  const returnTo = url.searchParams.get("return-to");
  const isAddAccountMode = mode === "add-account";

  // Check if user is already logged in
  const msSession = await getMsSession(request);

  // If not adding an account and already logged in, redirect
  if (!isAddAccountMode && msSession && msSession.accounts.length > 0) {
    return redirect(returnTo || "/projects");
  }

  // Get existing account email for display in add-account mode
  const existingAccountEmail = msSession?.accounts[0]?.email;

  // Get session to check for any messages and Turnstile cache
  const session = await getSession(request.headers.get("cookie"));

  // Capture signup context from URL params (for redirect after login)
  const affiliate = url.searchParams.get("affiliate");
  const promo = url.searchParams.get("promo");
  const message = url.searchParams.get("message");

  if (affiliate) {
    session.set("signupAffiliate", affiliate);
  }
  if (promo) {
    session.set("signupPromo", promo);
  }

  // Check if Turnstile challenge is required (uses 25 min threshold)
  const turnstileRequired = isTurnstileChallengeRequired(session, request);

  return {
    turnstileSiteKey: getTurnstileSiteKey(),
    turnstileRequired,
    message: message || undefined,
    isAddAccountMode,
    existingAccountEmail,
    returnTo: returnTo || undefined,
  };
};

interface ActionData {
  error?: string;
  success?: boolean;
}

export const action = async ({
  request,
}: ActionFunctionArgs): Promise<ActionData | Response> => {
  const url = new URL(request.url);
  const mode = url.searchParams.get("mode");
  const returnTo = url.searchParams.get("return-to");
  const isAddAccountMode = mode === "add-account";

  // Bots POST JSON or empty bodies here; answer 400 rather than letting the parse 500.
  let formData: FormData;
  try {
    formData = await request.formData();
  } catch {
    return Response.json({ error: "Invalid request." }, { status: 400 });
  }
  const email = formData.get("email") as string;
  const password = formData.get("password") as string;
  const rememberMe = formData.get("rememberMe") === "on";
  const clientIP = getClientIP(request);

  // Get session for Turnstile caching
  const session = await getSession(request.headers.get("cookie"));

  // Validate inputs
  if (!email || !password) {
    return { error: "Email and password are required." };
  }

  // Normalize the audit actor the same way login-attempt tracking keys the user
  // (recordLoginAttempt/authenticateUser lowercase internally). Otherwise the
  // same person logging in as "Bob@x.com" vs "bob@x.com" shows as two actors.
  const actor = email.trim().toLowerCase();

  // Check rate limiting before anything else
  const rateLimit = await checkLoginRateLimit(email);
  if (rateLimit.isLocked) {
    audit("login.rate_limited", {
      actor,
      ip: clientIP,
      outcome: "blocked",
    });
    return {
      error: `Too many failed login attempts. Please try again in ${rateLimit.lockoutMinutesRemaining} minute${rateLimit.lockoutMinutesRemaining === 1 ? "" : "s"}.`,
    };
  }

  // Verify Turnstile (uses session cache if available)
  const turnstileResult = await verifyTurnstileWithCache(
    formData,
    request,
    session,
  );
  if (!turnstileResult.success) {
    return { error: turnstileResult.error || "Bot verification failed." };
  }

  // Authenticate user
  const authResult = await authenticateUser(email, password);
  if (!authResult.success || !authResult.user) {
    // Record failed login attempt
    await recordLoginAttempt(email, false, clientIP);
    audit("login.failure", { actor, ip: clientIP, outcome: "failure" });

    // If Turnstile was freshly verified, commit session to save the cache
    if (turnstileResult.sessionUpdated) {
      return Response.json(
        { error: authResult.error || "Invalid email or password." },
        {
          headers: {
            "Set-Cookie": await commitSession(session),
          },
        },
      );
    }

    return { error: authResult.error || "Invalid email or password." };
  }

  // Record successful login and clear failed attempts
  await recordLoginAttempt(email, true, clientIP);
  await clearFailedLoginAttempts(email);
  audit("login.success", { actor, ip: clientIP, outcome: "success" });

  // Add session creation timestamp for invalidation on password change
  const userWithTimestamp = {
    ...authResult.user,
    sessionCreatedAt: Date.now(),
  };

  // Set session duration based on Remember Me
  const maxAge = rememberMe
    ? SESSION_MAX_AGE_REMEMBER
    : SESSION_MAX_AGE_DEFAULT;

  if (isAddAccountMode) {
    // Add account to existing session
    const cookie = await addAccountToSession(request, userWithTimestamp, true);

    // Redirect back to where they came from
    const finalRedirect = returnTo || "/projects";
    return redirect(finalRedirect, {
      headers: {
        "Set-Cookie": cookie,
      },
    });
  }

  // Set user in session (reusing session already fetched for Turnstile caching)
  session.set("user", userWithTimestamp);

  // Check for redirect URL (return-to query param takes priority)
  const redirectTo = returnTo || session.get("redirectTo") || "/projects";
  session.unset("redirectTo");

  // Role-based default redirect for system users
  let finalRedirect = redirectTo;
  if (!returnTo) {
    if (authResult.user.isSystemAdmin) {
      finalRedirect = "/admin/dashboard";
    } else if (authResult.user.isSupportStaff) {
      finalRedirect = "/admin/support";
    } else if (authResult.user.isEngineer) {
      finalRedirect = "/admin/developer-tools";
    }
  }

  return redirect(finalRedirect, {
    headers: {
      "Set-Cookie": await commitSession(session, { maxAge }),
    },
  });
};

export default function Login() {
  const loaderData = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  // Handle case where loader returns a redirect
  if (!loaderData || "status" in loaderData) {
    return null;
  }

  const {
    turnstileSiteKey,
    turnstileRequired,
    message,
    isAddAccountMode,
    existingAccountEmail,
  } = loaderData;

  return (
    <main className="flex min-h-screen items-center justify-center bg-ms-gray-50 px-4 py-12">
      {/* Login card */}
      <div className="w-full max-w-md rounded-2xl border border-ms-gray-200 bg-white p-8 shadow-lg">
        {/* Header */}
        <div className="mb-8 text-center">
          <img
            src="/images/mediascribe-logo.svg"
            alt="MediaScribe"
            className="mx-auto mb-6 h-10 w-auto"
          />
          {isAddAccountMode ? (
            <>
              <h1 className="font-serif text-2xl text-ms-gray-900">
                Add another account
              </h1>
              {existingAccountEmail && (
                <p className="mt-2 text-sm text-ms-gray-500">
                  Currently signed in as {existingAccountEmail}
                </p>
              )}
            </>
          ) : (
            <h1 className="font-serif text-2xl text-ms-gray-900">
              Welcome back
            </h1>
          )}
        </div>

        {/* Success message */}
        {message && (
          <Alert variant="success" className="mb-6">
            {message}
          </Alert>
        )}

        {/* Error message */}
        {actionData?.error && (
          <Alert variant="error" className="mb-6">
            {actionData.error}
          </Alert>
        )}

        <Form method="post" className="space-y-5">
          {/* Email */}
          <FormInput
            type="email"
            name="email"
            label="Email"
            required
            autoComplete="email"
            placeholder="you@example.com"
          />

          {/* Password */}
          {/*
            "Forgot password?" is rendered AFTER the input and positioned back up
            onto the label's row, rather than sitting in a flex row before it.
            Tab order follows DOM order, so the previous markup sent Tab from
            Email to the forgot-password link instead of to the password field.
            Keep the link last here — it stays fully keyboard reachable (Tab from
            the password field), it just isn't in the way of typing a password.
          */}
          <div className="relative">
            <label
              htmlFor="password"
              className="mb-2 block text-sm font-medium text-ms-gray-700"
            >
              Password
            </label>
            <FormInput
              type="password"
              id="password"
              name="password"
              required
              autoComplete="current-password"
            />
            <Link
              to="/auth/forgot-password"
              className="absolute right-0 top-0 text-sm text-ms-primary-600 transition-colors hover:text-ms-primary-500"
            >
              Forgot password?
            </Link>
          </div>

          {/* Remember Me */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              className="h-4 w-4 rounded border-ms-gray-300 bg-white text-ms-primary-600 focus:ring-2 focus:ring-ms-primary-500 focus:ring-offset-2"
            />
            <label
              htmlFor="rememberMe"
              className="ml-3 block text-sm text-ms-gray-600"
            >
              Remember me for 30 days
            </label>
          </div>

          {/* Turnstile */}
          <TurnstileWidget
            siteKey={turnstileSiteKey || ""}
            className="mt-4"
            resetKey={actionData?.error}
            skip={!turnstileRequired}
          />

          {/* Submit */}
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            loadingText="Signing in..."
            className="w-full"
            size="md"
          >
            Sign in
          </Button>
        </Form>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-ms-gray-600">
          Don&apos;t have an account?{" "}
          <Link
            to="/auth/signup"
            className="font-medium text-ms-primary-600 transition-colors hover:text-ms-primary-500"
          >
            Create an account
          </Link>
        </p>
      </div>
    </main>
  );
}
